import { useEffect, useRef } from 'react'
import { useArrived, useTooltip } from './Tooltip'

// The days ahead as a line: a brand marker at today, four date ticks, and a dot
// for every date something is due, coloured by how pressing it is. Dots on the
// same day stack upward rather than hiding each other, and each one opens the
// thing that is due.
//
// The span is the period's: 7, 30 or 90 days. When it changes the ticks and
// every dot that is still due slide to where the new scale puts them, and a dot
// the new horizon brings in pops in where it lands, so switching the period is
// seen to move the strip rather than redraw it.

export type DueDot = { date: string; title: string; subject: string; tone: string; word: string; href?: string }

export const TONE_SOLID: Record<string, string> = {
  crit: 'var(--crit-solid)',
  warn: 'var(--warn-solid)',
  info: 'var(--info-solid)',
  neutral: 'var(--neu-solid)',
  ok: 'var(--ok-solid)',
  brand: 'var(--brand)',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function dayMonthUTC(d: Date): string {
  return String(d.getUTCDate()).padStart(2, '0') + ' ' + MONTHS[d.getUTCMonth()]
}

function dayIndex(iso: string, today: Date): number {
  const d = new Date(iso + 'T00:00:00Z')
  const t = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  return Math.round((d.getTime() - t) / 86400000)
}

export function DeadlineStrip({ items, today, span = 30 }: { items: DueDot[]; today: Date; span?: number }) {
  const arrived = useArrived()
  const tip = useTooltip()
  // Today, a third, two thirds and the end of the span, on whole days.
  const ticks = [0, Math.round(span / 3), Math.round((2 * span) / 3), span].map((k) => {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + k))
    return { k, label: dayMonthUTC(d) }
  })
  const perDay = new Map<number, number>()
  // The dots on the strip as last drawn, so one that arrives with a new answer
  // pops in and one that was already there slides. Recorded after the strip is
  // on screen, never during a render, so a render React throws away cannot
  // mark a dot as seen.
  const seen = useRef<Set<string> | null>(null)
  const keys = new Map<string, number>()
  const keyOf = (it: DueDot) => {
    const base = it.date + '|' + it.title + '|' + it.subject
    const n = keys.get(base) ?? 0
    keys.set(base, n + 1)
    return base + '|' + n
  }
  const first = seen.current == null
  const drawn = new Set<string>()

  const dots = items.map((it, i) => {
    const key = keyOf(it)
    drawn.add(key)
    const day = Math.max(0, Math.min(span, dayIndex(it.date, today)))
    const stack = perDay.get(day) ?? 0
    perDay.set(day, stack + 1)
    if (stack > 2) return null
    const color = TONE_SOLID[it.tone] ?? 'var(--neu-solid)'
    const when = day === 0 ? 'Today' : dayMonthUTC(new Date(it.date + 'T00:00:00Z'))
    const rows = [{ label: it.title, value: it.word, color }]
    const fresh = !first && !seen.current!.has(key)
    return (
      <button
        key={key}
        type="button"
        className={'dot' + (fresh ? ' fresh' : '')}
        style={{ left: (day / span) * 100 + '%', background: color, marginTop: -stack * 16, transitionDelay: first ? i * 90 + 'ms' : '0ms' }}
        aria-label={when + ': ' + it.title + ', ' + it.subject + ', ' + it.word}
        onClick={() => {
          if (it.href) window.location.hash = it.href.replace(/^#/, '')
        }}
        onPointerMove={(e) => tip.show(e, when, rows)}
        onPointerLeave={() => tip.hide()}
        onFocus={(e) => tip.showAt(e.currentTarget, when, rows)}
        onBlur={() => tip.hide()}
      />
    )
  })
  useEffect(() => {
    seen.current = drawn
  })

  return (
    <div className={'hc-cal' + (arrived ? ' on' : '')}>
      <div className="axisline">
        <span className="today" style={{ left: '0%' }} />
        {ticks.map((t, i) => (
          <span key={i} className={'tick' + (i === 0 ? ' first' : i === ticks.length - 1 ? ' last' : '')} style={{ left: (t.k / span) * 100 + '%' }}>
            {t.label}
          </span>
        ))}
        {dots}
      </div>
    </div>
  )
}
